import { useEffect } from 'react';
import { useUpdateUserSetting, useUserSettings } from '../../../hooks/useUserSettings';
import { useToast } from '../../../providers/ToastProvider';
import { getSettingValue } from '../../../services/userSettings';
import { apiErrorMessage } from '../../../utils/apiError';
import { UDToggle } from './UDToggle';

interface UserNotificationSettingsSectionProps {
    userId: string;
}

interface NotificationToggleConfig {
    key: string;
    title: string;
    desc: string;
}

const NOTIFICATION_TOGGLES: NotificationToggleConfig[] = [
    {
        key: 'chat_activity',
        title: 'Chat notifications',
        desc: "Email this user when ODO posts a new message on a patient's chat."
    },
    {
        key: 'digest_emails',
        title: 'Practice digest emails',
        desc: 'Email this user a report about patients waiting on them.'
    }
];

export function UserNotificationSettingsSection({ userId }: UserNotificationSettingsSectionProps) {
    const { showToast } = useToast();
    const { settings, isLoading, isError, error } = useUserSettings(userId);
    const updateSetting = useUpdateUserSetting(userId);

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load notification settings'), 'error');
        }
    }, [isError, error, showToast]);

    const handleToggle = async (category: string, key: string, next: boolean) => {
        try {
            await updateSetting.mutateAsync({ category, key, value: next });
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to update setting'), 'error');
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                {NOTIFICATION_TOGGLES.map((toggle) => (
                    <div key={toggle.key} className="flex items-center gap-3">
                        <div className="w-9 h-5 rounded-full bg-gray-200 animate-pulse shrink-0" />
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                    </div>
                ))}
            </div>
        );
    }

    if (isError) {
        return <p className="text-sm text-red-500">Failed to load settings</p>;
    }

    return (
        <div className="flex flex-col gap-4">
            {NOTIFICATION_TOGGLES.map((toggle) => (
                <UDToggle
                    key={toggle.key}
                    on={getSettingValue(settings, 'notifications', toggle.key)}
                    onChange={(next) => void handleToggle('notifications', toggle.key, next)}
                    disabled={updateSetting.isPending}
                    title={toggle.title}
                    desc={toggle.desc}
                />
            ))}
        </div>
    );
}
